import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import adminApi from '../utils/adminApi';

export default function AdminTransfer() {
  const [amount, setAmount] = useState('');
  const [destination, setDestination] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    // Fetch users with stripe accounts
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const data = await adminApi.get('/api/manage/users?limit=100');
      // Filter users who have stripe accounts
      const usersWithStripe = (data.users || []).filter(user => user.stripe_account_id);
      setUsers(usersWithStripe);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  };

  const handleTransfer = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      const data = await adminApi.post('/api/manage/transfer', {
        amount: Math.round(parseFloat(amount) * 100), // Convert to cents
        destination: destination,
        currency: 'usd',
        description: description || 'Admin transfer'
      });

      setMessage({
        type: 'success',
        text: `Transfer successful! ID: ${data.id}, Status: ${data.status}`
      });

      // Clear form
      setAmount('');
      setDescription('');
      
    } catch (err) {
      setMessage({
        type: 'error',
        text: err.message || 'Transfer failed'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white flex flex-col items-center p-10">
      <div className="w-full max-w-2xl">
        <Link to="/manage" className="inline-flex items-center space-x-2 text-gray-400 hover:text-white mb-6 transition">
          <FaArrowLeft />
          <span>Back to Admin Dashboard</span>
        </Link>
        <h1 className="text-3xl font-bold mb-8 text-center">Admin Transfer Tool</h1>
        
        <div className="bg-gray-800 p-6 rounded-xl border border-blue-500 mb-6">
          <h2 className="text-xl font-bold mb-4">Direct Transfer to Connected Account</h2>
          
          <div className="bg-yellow-900 border border-yellow-600 p-4 rounded-lg mb-6">
            <p className="text-yellow-200 text-sm">
              <strong>⚠️ Important:</strong> Stripe transfers only work with funds collected through your platform. 
              You cannot transfer arbitrary amounts from your Stripe account.
            </p>
          </div>

          {message && (
            <div className={`mb-4 p-4 rounded-lg ${
              message.type === 'success' 
                ? 'bg-green-900 text-green-200' 
                : 'bg-red-900 text-red-200'
            }`}>
              {message.text}
            </div>
          )}

          <form onSubmit={handleTransfer}>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">
                Amount ($)
              </label>
              <input
                type="number"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full p-3 bg-gray-900 border border-gray-600 rounded-lg text-white"
                placeholder="50.00"
                required
                min="0.01"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">
                Destination Account
              </label>
              <select
                value={destination}
                onChange={(e) => setDestination(e.target.value)}
                className="w-full p-3 bg-gray-900 border border-gray-600 rounded-lg text-white"
                required
              >
                <option value="">Select a connected account...</option>
                {users.map(user => (
                  <option key={user.id} value={user.stripe_account_id}>
                    {user.first_name} {user.last_name} - {user.stripe_account_id}
                  </option>
                ))}
              </select>
              
              <div className="mt-2">
                <input
                  type="text"
                  value={destination}
                  onChange={(e) => setDestination(e.target.value)}
                  className="w-full p-3 bg-gray-900 border border-gray-600 rounded-lg text-white"
                  placeholder="Or enter account ID manually (acct_...)"
                />
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium mb-2">
                Description (Optional)
              </label>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full p-3 bg-gray-900 border border-gray-600 rounded-lg text-white"
                placeholder="Transfer description"
              />
            </div>

            <button
              type="submit"
              disabled={loading || !amount || !destination}
              className="w-full bg-blue-500 hover:bg-blue-600 disabled:bg-gray-600 text-white font-semibold py-3 px-4 rounded-lg transition"
            >
              {loading ? 'Processing...' : 'Initiate Transfer'}
            </button>
          </form>
        </div>

        <div className="bg-gray-800 p-6 rounded-xl border border-gray-600">
          <h3 className="text-lg font-bold mb-4">Connected Accounts</h3>
          {users.length === 0 ? (
            <p className="text-gray-400">No users with connected Stripe accounts found.</p>
          ) : (
            <div className="space-y-2">
              {users.map(user => (
                <div key={user.id} className="p-3 bg-gray-900 rounded-lg">
                  <p className="font-medium">{user.first_name} {user.last_name}</p>
                  <p className="text-sm text-gray-400">{user.email}</p>
                  <p className="text-xs text-blue-400 font-mono">{user.stripe_account_id}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}